import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Label } from "@wondermaps/ui";
import { api } from "../lib/api";
import { useAuditLog } from "../hooks/useAuditLog";
import { AuditLogTable, AuditLogFilters } from "../components/audit-log";

export const Route = createFileRoute("/superadmin/audit-log/")({
  component: SuperadminAuditLogPage,
});

type Organization = {
  id: string;
  name: string;
  slug: string;
};

function SuperadminAuditLogPage() {
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState("all");
  const [action, setAction] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [source, setSource] = useState<"content" | "security" | "all">("content");
  const [selectedOrgId, setSelectedOrgId] = useState<string>("all");

  // Fetch organizations for filter
  const { data: orgsData } = useQuery({
    queryKey: ["superadmin", "orgs"],
    queryFn: async () => {
      const res = await api.orgs.admin.$get();
      if (!res.ok) throw new Error("Failed to fetch organizations");
      return res.json() as Promise<{ orgs: Organization[] }>;
    },
  });

  const orgs = orgsData?.orgs ?? [];

  // Fetch audit logs - no orgId means global view
  const { data, isLoading } = useAuditLog({
    page,
    limit: 50,
    entityType: entityType !== "all" ? entityType : undefined,
    action: action !== "all" ? action : undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    source,
  });

  // Filter entries by selected org if needed
  const entries = selectedOrgId !== "all"
    ? (data?.entries ?? []).filter((e) => e.orgId === selectedOrgId)
    : (data?.entries ?? []);

  return (
    <div className="space-y-6">
      {/* Org filter */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Organization</Label>
          <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All organizations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All organizations</SelectItem>
              {orgs.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Standard filters */}
      <AuditLogFilters
        entityType={entityType}
        setEntityType={setEntityType}
        action={action}
        setAction={setAction}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        source={source}
        setSource={setSource}
      />

      {/* Table */}
      <AuditLogTable
        entries={entries}
        isLoading={isLoading}
        page={page}
        pageCount={data?.pageCount ?? 1}
        total={selectedOrgId !== "all" ? entries.length : (data?.total ?? 0)}
        onPageChange={setPage}
        showOrgColumn
      />
    </div>
  );
}
